"use client";

import { useState, useRef, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Input,
  InputGroup,
  InputGroupText,
  Pagination,
  PaginationItem,
  PaginationLink,
  Badge,
  Spinner,
  Alert,
} from "reactstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Api from "../../api/Api";

// Form validation schema
const formSchema = yup.object().shape({
  files: yup.mixed(),
});

export default function RoomsGallary() {
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const fileInputRef = useRef(null);

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(formSchema),
  });

  // Fetch images from API on component mount
  useEffect(() => {
    fetchImages();
  }, []);

  // Update filtered images when images or search query changes
  useEffect(() => {
    if (searchQuery) {
      const filtered = images.filter((image) =>
        image.id.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredImages(filtered);
    } else {
      setFilteredImages(images);
    }
  }, [images, searchQuery]);

  // Fetch images from API
  const fetchImages = async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const response = await Api.get("/roomsimages");
      console.log(response.data);

      if (response.data && response.data.data) {
        // Transform API response to match our format
        const apiImages = response.data.data.data.map((img) => ({
          id: img.id.toString(),
          imageUrl: img.url || img.path,
          title: img.title || "",
          createdAt: img.created_at,
        }));
        setImages(apiImages);
      } else {
        // Handle empty response
        setImages([]);
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      setLoadError("Failed to load images. Please try again later.");
      setImages([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle multiple image upload
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      setSelectedFiles(filesArray);

      // Create preview URLs for all selected files
      const newPreviewUrls = [];

      filesArray.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviewUrls.push({
            id: Math.random().toString(36).substring(2, 9),
            url: reader.result,
            file: file,
          });
          if (newPreviewUrls.length === filesArray.length) {
            setPreviewUrls(newPreviewUrls);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // Remove a single preview image
  const removePreviewImage = (id) => {
    setPreviewUrls(previewUrls.filter((preview) => preview.id !== id));

    // If we've removed all previews, also clear the file input
    if (previewUrls.length === 1) {
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Send images to Laravel API using Axios
  const sendImagesToApi = async (files) => {
    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    setUploadProgress(0);

    try {
      // Create FormData to send files
      const formData = new FormData();

      // Add each file to the FormData
      files.forEach((fileObj, index) => {
        formData.append(`images[]`, fileObj.file);
      });

      // Send the request to your Laravel API using axios
      const response = await Api.post("/roomsimages/upload", formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      // Handle successful response
      console.log("Upload successful:", response.data);
      setUploadSuccess(true);

      // Add the uploaded images to the gallery (with URLs from the API response)
      if (response.data && response.data.images) {
        const newImages = response.data.images.map((image) => ({
          id: image.id.toString(),
          imageUrl: image.url || image.path,
        }));

        setImages([...images, ...newImages]);
      }

      // Reset form and state
      setTimeout(() => {
        reset();
        setSelectedFiles([]);
        setPreviewUrls([]);
        setIsAddModalOpen(false);
        setUploadSuccess(false);
      }, 1500);
    } catch (error) {
      console.error("Upload error:", error);
      setUploadError(
        error.response?.data?.message ||
          error.message ||
          "Failed to upload images. Please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  // Handle form submission
  const onSubmit = () => {
    // Validate that we have at least one image
    if (previewUrls.length === 0) {
      setUploadError("Please select at least one image");
      return;
    }

    // Send images to API
    sendImagesToApi(previewUrls);
  };

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
  };

  // Handle checkbox selection
  const handleCheckboxChange = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Handle select all checkboxes
  const handleSelectAll = () => {
    if (selectedItems.length === currentItems.length) {
      // If all are selected, unselect all
      setSelectedItems([]);
    } else {
      // Otherwise, select all current items
      setSelectedItems(currentItems.map((item) => item.id));
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    try {
      // Call API to delete the image
      await Api.delete(`/roomsimages/${id}`);

      // Update local state
      setImages(images.filter((image) => image.id !== id));
      setSelectedItems(selectedItems.filter((item) => item !== id));
    } catch (error) {
      console.error("Error deleting image:", error);
      // Show error message
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    try {
      // Call API to delete multiple images
      await Api.post("/roomsimages/bulk-delete", { ids: selectedItems });

      // Update local state
      const updatedImages = images.filter(
        (image) => !selectedItems.includes(image.id)
      );
      setImages(updatedImages);
      setSelectedItems([]);
    } catch (error) {
      console.error("Error deleting images:", error);
      // Show error message
    }
  };

  // Handle view image
  const handleViewImage = (image) => {
    setCurrentImage(image);
    setIsViewModalOpen(true);
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredImages.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredImages.length / itemsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbers = 5;
    const maxVisiblePages = Math.min(totalPages, maxPageNumbers);

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="h3">Room Gallery</h1>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#">Home</a>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Room Gallery
              </li>
            </ol>
          </nav>
        </div>

        <Card>
          <CardBody>
            <div className="mb-4">
              <Row className="g-4 mb-3">
                <Col className="col-sm-auto">
                  <div className="d-flex gap-1">
                    <Button
                      color="success"
                      onClick={() => setIsAddModalOpen(true)}
                    >
                      <i className="ri-add-line align-bottom me-1"></i> Add
                      Images
                    </Button>
                    {selectedItems.length > 0 && (
                      <Button color="danger" onClick={handleBulkDelete}>
                        <i className="ri-delete-bin-line align-bottom me-1"></i>{" "}
                        Delete Selected ({selectedItems.length})
                      </Button>
                    )}
                  </div>
                </Col>
                <Col className="col-sm">
                  <div className="d-flex justify-content-sm-end">
                    <div className="search-box ms-2">
                      <InputGroup>
                        <InputGroupText>
                          <i className="ri-search-line"></i>
                        </InputGroupText>
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder="Search..."
                          value={searchQuery}
                          onChange={handleSearch}
                        />
                      </InputGroup>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Loading State */}
              {isLoading && (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2">Loading images...</p>
                </div>
              )}

              {/* Error State */}
              {loadError && !isLoading && (
                <Alert color="danger" className="my-3">
                  {loadError}
                </Alert>
              )}

              {/* Select All Checkbox */}
              {!isLoading && currentItems.length > 0 && (
                <div className="d-flex align-items-center mb-3">
                  <div className="form-check">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="selectAll"
                      checked={
                        selectedItems.length === currentItems.length &&
                        currentItems.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                    <label className="form-check-label" htmlFor="selectAll">
                      Select All
                    </label>
                  </div>
                  {selectedItems.length > 0 && (
                    <Badge color="info" className="ms-2">
                      {selectedItems.length} selected
                    </Badge>
                  )}
                </div>
              )}

              {/* Gallery Grid */}
              {!isLoading && (
                <Row>
                  {currentItems.map((image) => (
                    <Col
                      key={image.id}
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      className="mb-4"
                    >
                      <div className="position-relative">
                        <div className="position-absolute top-0 start-0 m-2 z-index-1">
                          <Input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedItems.includes(image.id)}
                            onChange={() => handleCheckboxChange(image.id)}
                          />
                        </div>
                        <Card className="h-100">
                          <div
                            className="gallery-image cursor-pointer"
                            style={{
                              height: "200px",
                              backgroundImage: `url(${image.imageUrl})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                              cursor: "pointer",
                            }}
                            onClick={() => handleViewImage(image)}
                          ></div>
                          <CardBody className="p-2">
                            <div className="d-flex justify-content-end">
                              <Button
                                color="link"
                                className="text-danger p-0"
                                onClick={() => handleDelete(image.id)}
                              >
                                <i className="ri-delete-bin-line"></i>
                              </Button>
                            </div>
                          </CardBody>
                        </Card>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}

              {/* Empty State */}
              {!isLoading && currentItems.length === 0 && !loadError && (
                <div className="text-center py-5">
                  <div className="display-6 text-muted mb-3">
                    <i className="ri-image-line"></i>
                  </div>
                  <h5 className="text-muted mb-3">No images found</h5>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => setIsAddModalOpen(true)}
                  >
                    Add Images
                  </Button>
                </div>
              )}

              {/* Pagination */}
              {!isLoading && totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <PaginationItem disabled={currentPage === 1}>
                      <PaginationLink previous onClick={prevPage} />
                    </PaginationItem>

                    {getPageNumbers().map((number) => (
                      <PaginationItem
                        key={number}
                        active={currentPage === number}
                      >
                        <PaginationLink onClick={() => setCurrentPage(number)}>
                          {number}
                        </PaginationLink>
                      </PaginationItem>
                    ))}

                    <PaginationItem disabled={currentPage === totalPages}>
                      <PaginationLink next onClick={nextPage} />
                    </PaginationItem>
                  </Pagination>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </Container>

      {/* Add Images Modal */}
      <Modal
        isOpen={isAddModalOpen}
        toggle={() => setIsAddModalOpen(!isAddModalOpen)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setIsAddModalOpen(!isAddModalOpen)}>
          Add New Images
        </ModalHeader>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            {/* Error and Success Messages */}
            {uploadError && (
              <Alert color="danger" className="mb-3">
                {uploadError}
              </Alert>
            )}

            {uploadSuccess && (
              <Alert color="success" className="mb-3">
                Images uploaded successfully!
              </Alert>
            )}

            <div className="mb-3">
              <div
                className="border border-dashed rounded p-4 text-center cursor-pointer"
                style={{ backgroundColor: "#f8f9fa" }}
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="ri-upload-cloud-line display-4 text-muted"></i>
                <p className="mt-2">Click to upload or drag and drop</p>
                <p className="small text-muted">PNG, JPG, GIF up to 10MB</p>
                <p className="small text-primary">
                  You can select multiple images
                </p>
                <Input
                  type="file"
                  innerRef={fileInputRef}
                  className="d-none"
                  accept="image/*"
                  multiple
                  {...register("files")}
                  onChange={handleImageChange}
                />
              </div>
              {errors.files && (
                <div className="text-danger small mt-1">
                  {errors.files.message}
                </div>
              )}
            </div>

            {/* Upload Progress */}
            {isUploading && (
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>Upload Progress</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="progress" style={{ height: "20px" }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${uploadProgress}%` }}
                    aria-valuenow={uploadProgress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </div>
            )}

            {/* Preview of selected images */}
            {previewUrls.length > 0 && (
              <div className="mt-4">
                <h6 className="mb-3">Selected Images ({previewUrls.length})</h6>
                <Row className="g-2">
                  {previewUrls.map((preview) => (
                    <Col key={preview.id} xs={6} md={4} lg={3}>
                      <div className="position-relative">
                        <img
                          src={preview.url || "/placeholder.svg"}
                          alt="Preview"
                          className="img-thumbnail"
                          style={{
                            height: "120px",
                            objectFit: "cover",
                            width: "100%",
                          }}
                        />
                        <Button
                          color="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 rounded-circle p-1"
                          style={{ margin: "2px" }}
                          onClick={() => removePreviewImage(preview.id)}
                          disabled={isUploading}
                        >
                          <i className="ri-close-line"></i>
                        </Button>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              onClick={() => setIsAddModalOpen(false)}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={isUploading || previewUrls.length === 0}
            >
              {isUploading ? (
                <>
                  <Spinner size="sm" className="me-2" /> Uploading...
                </>
              ) : (
                <>
                  Add{" "}
                  {previewUrls.length > 0
                    ? `${previewUrls.length} Images`
                    : "Images"}
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* View Image Modal */}
      <Modal
        isOpen={isViewModalOpen}
        toggle={() => setIsViewModalOpen(!isViewModalOpen)}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => setIsViewModalOpen(!isViewModalOpen)}>
          Image Preview
        </ModalHeader>
        <ModalBody className="text-center">
          {currentImage && (
            <img
              src={currentImage.imageUrl || "/placeholder.svg"}
              alt="Full size"
              className="img-fluid"
              style={{ maxHeight: "70vh" }}
            />
          )}
        </ModalBody>
      </Modal>
    </div>
  );
}
